import { useRef, useState } from 'react';
import { RESPONSE_ATTACHMENT_DOCUMENT_TYPE, RESPONSE_MAX_ATTACHMENTS, isResponseAttachable } from '@pqrs/contracts';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { SelectorMultiple } from '@xiltrion/ui/bloques/selectores';
import { Field } from '@/components/ui';
import { useCaseDocuments, useUploadDocuments } from '@/features/cases/api';
import { CaseFilesDrop, RejectedFilesAlert } from '@/features/cases/CaseFilesDrop';
import { errorMessage, fmtBytes } from '@/features/cases/lib';

/**
 * Anexos de la respuesta: archivos que quien responde sube aquí (entran al expediente como «Anexo de la respuesta») o
 * documentos que un funcionario ya subió al expediente. Salen con la respuesta: adjuntos al correo o, si el envío es
 * certificado (Datacrédito Postal no acepta archivos), como un enlace para descargar cada uno.
 */
export function ResponseAttachments({ caseId, value, onChange, disabled }: { caseId: string; value: string[]; onChange: (ids: string[]) => void; disabled?: boolean }) {
  const docs = useCaseDocuments(caseId);
  const upload = useUploadDocuments(caseId);
  const [subiendo, setSubiendo] = useState<File[]>([]);
  // La subida termina después: se suma a lo elegido en ese momento, no a lo que había al empezar.
  const latest = useRef(value);
  latest.current = value;
  const attachable = (docs.data ?? []).filter(isResponseAttachable);
  const chosen = value.map((id) => docs.data?.find((d) => d.id === id)).filter((d) => !!d);
  const full = value.length >= RESPONSE_MAX_ATTACHMENTS;
  const subir = (archivos: File[]) => {
    const nuevos = archivos.filter((f) => !subiendo.includes(f)).slice(0, RESPONSE_MAX_ATTACHMENTS - value.length);
    setSubiendo(archivos);
    if (!nuevos.length) return;
    upload.mutate({ files: nuevos, documentType: RESPONSE_ATTACHMENT_DOCUMENT_TYPE }, {
      onSuccess: (r) => onChange([...latest.current, ...r.stored.map((d) => d.id)].slice(0, RESPONSE_MAX_ATTACHMENTS)),
      onSettled: () => setSubiendo((s) => s.filter((f) => !nuevos.includes(f))),
    });
  };

  if (disabled) return chosen.length ? <ResponseAttachmentsView caseId={caseId} ids={value} /> : null;
  return (
    <Field label="Anexos de la respuesta (opcional)" htmlFor="response-annexes" hint={`Hasta ${RESPONSE_MAX_ATTACHMENTS}. Salen con la respuesta: adjuntos al correo o, si el envío es certificado, como un enlace para descargar cada uno. Los que subes aquí quedan en el expediente como «${RESPONSE_ATTACHMENT_DOCUMENT_TYPE}».`}>
      <div className="flex flex-col gap-3">
        {chosen.length > 0 && (
          <ul className="list-none m-0 p-0 flex flex-col gap-1 text-sm">
            {chosen.map((d) => (
              <li key={d.id} className="flex items-center gap-2">
                <Icono nombre="mdi:paperclip" aria-hidden />
                <a href={`/api/v1/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="truncate flex-1">{d.fileName}</a>
                <span className="text-xs text-muted-foreground tnum">{fmtBytes(d.sizeBytes)}</span>
                <Button type="button" variant="ghost" size="icon-sm" aria-label={`Quitar ${d.fileName} de la respuesta`} onClick={() => onChange(value.filter((x) => x !== d.id))}><Icono nombre="mdi:close" /></Button>
              </li>
            ))}
          </ul>
        )}
        {!full && <CaseFilesDrop archivos={subiendo} onArchivosChange={subir} deshabilitado={upload.isPending} maxArchivos={RESPONSE_MAX_ATTACHMENTS - value.length}
          titulo={upload.isPending ? 'Subiendo…' : 'Adjuntar anexos'} aria-label="Adjuntar anexos a la respuesta" />}
        {attachable.length > 0 && (
          <SelectorMultiple id="response-annexes" value={value.filter((id) => attachable.some((d) => d.id === id))} textoFijo="Elegir del expediente" buscable={attachable.length > 8} placeholderBusqueda="Buscar documento…"
            options={attachable.map((d) => ({ value: d.id, label: d.fileName, extra: fmtBytes(d.sizeBytes), deshabilitada: full && !value.includes(d.id) }))}
            onChange={(ids) => onChange([...value.filter((id) => ids.includes(id) || !attachable.some((d) => d.id === id)), ...ids.filter((id) => !value.includes(id))].slice(0, RESPONSE_MAX_ATTACHMENTS))} className="self-start" />
        )}
        <RejectedFilesAlert files={upload.data?.failed ?? []} title="El servidor no recibió estos archivos" />
        {upload.isError && <Alert variant="destructive"><AlertDescription>No pudimos subir los anexos: {errorMessage(upload.error)}</AlertDescription></Alert>}
      </div>
    </Field>
  );
}

/** Los anexos de una respuesta, para quien la revisa o la firma. */
export function ResponseAttachmentsView({ caseId, ids }: { caseId: string; ids: string[] | undefined }) {
  const docs = useCaseDocuments(caseId, !!ids?.length);
  const chosen = (ids ?? []).map((id) => docs.data?.find((d) => d.id === id)).filter((d) => !!d);
  if (!chosen.length) return null;
  return (
    <div>
      <div className="text-sm font-medium mb-1">Anexos de la respuesta ({chosen.length})</div>
      <ul className="list-none m-0 p-0 flex flex-col gap-1 text-sm">
        {chosen.map((d) => <li key={d.id} className="flex items-center gap-2"><Icono nombre="mdi:paperclip" aria-hidden /><a href={`/api/v1/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="truncate flex-1">{d.fileName}</a><span className="text-xs text-muted-foreground tnum">{fmtBytes(d.sizeBytes)}</span></li>)}
      </ul>
    </div>
  );
}
